import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbInputModule,
  NbSelectModule,
} from '@nebular/theme';
import { Turma } from '../../../core/api/turma';
import { Itinerario } from '../../../core/api/itinerario';
import { TurmaService } from '../../../core/api/turma.service';
import { ItinerarioService } from '../../../core/api/itinerario.service';
import { DialogAlertComponent } from '../../inscricao/inscricao.dialog.component';
import { TurmaDto } from '../../../core/api/turmaDto';
import { AutocompleteComponent, OpcaoAutocomplete } from '../../../shared/autocomplete/autocomplete.component';

@Component({
  selector: 'app-turma-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbInputModule,
    NbButtonModule,
    NbSelectModule,
    NbCheckboxModule,
    AutocompleteComponent,
  ],
  templateUrl: './turma-form.component.html',
  styleUrls: ['./turma-form.component.scss'],
})
export class TurmaFormComponent implements OnInit {
  /**
   * `MSG-11` — formulário inválido marca **todos** os campos como tocados ao tentar
   * salvar, para a tela mostrar de uma vez tudo o que falta. Botão que não responde ao
   * clique é lido como sistema travado.
   */
  @ViewChild('formTurma') protected formulario?: NgForm;

  private formularioValido(): boolean {
    if (this.formulario && this.formulario.invalid) {
      this.formulario.control.markAllAsTouched();
      this.dialogService.open(DialogAlertComponent, {
        context: {
          title: 'Faltam campos',
          message: 'Preencha os campos marcados com * antes de salvar. Eles estão destacados no formulário.',
        },
      });
      return false;
    }
    return true;
  }

  @Input() turma: Turma = new Turma();

  itinerarios: Itinerario[] = [];

  /**
   * `FRM-01` — itinerário vem de `GET /itinerarios`, então é autocomplete e não lista
   * suspensa, ainda que hoje a lista seja curta. O filtro é em memória porque o cadastro é
   * curto e fechado e o contrato não aceita busca textual, que é o caso que `FRM-04`
   * dispensa de ir ao servidor.
   */
  get opcoesItinerario(): OpcaoAutocomplete[] {
    return this.itinerarios.map((i) => ({ id: i.id ?? '', rotulo: i.nome ?? '' }));
  }
  /** Id do itinerário selecionado ('' = nenhum). */
  itinerarioId = '';

  constructor(
    private turmaService: TurmaService,
    private itinerarioService: ItinerarioService,
    protected ref: NbDialogRef<TurmaFormComponent>,
    private dialogService: NbDialogService
  ) {}

  ngOnInit(): void {
    if (this.turma.ativo === undefined || this.turma.ativo === null) {
      this.turma.ativo = true;
    }
    // Carrega os itinerários e só então marca o selecionado. O value de cada opção
    // é o id (que é o que trafega na API); o texto exibido é o nome do itinerário.
    // A seleção é aplicada em um tick posterior (setTimeout) para o nb-select já ter
    // registrado as <nb-option> do *ngFor — senão ele não acha a opção e mostra o
    // placeholder em vez do nome.
    this.itinerarioService.listarTodos().subscribe({
      next: (itinerarios) => {
        this.itinerarios = itinerarios;
        const id = this.turma.itinerarioId ?? this.turma.itinerario?.id ?? '';
        setTimeout(() => (this.itinerarioId = id));
      },
    });
  }

  private toDto(turma: Turma): TurmaDto {
    return {
      nome: turma.nome ?? '',
      diaSemana: turma.diaSemana ?? '',
      horarioInicio: turma.horarioInicio ?? '',
      horarioFim: turma.horarioFim ?? '',
      ano: turma.ano ?? new Date().getFullYear(),
      ativo: turma.ativo ?? true,
      // sempre envia o campo: id selecionado ou null para limpar o vínculo
      itinerarioId: this.itinerarioId ? this.itinerarioId : null,
    };
  }

  salvar() {
    if (!this.formularioValido()) {
      return;
    }

    const dto = this.toDto(this.turma);

    if (this.turma.id) {
      this.turmaService.atualizar(this.turma.id, dto).subscribe(() => {
        this.ref.close(true);
        this.dialogService.open(DialogAlertComponent, {
          context: { title: 'Sucesso', message: 'Turma atualizada com sucesso!' },
        });
      });
    } else {
      this.turmaService.criar(dto).subscribe(() => {
        this.ref.close(true);
        this.dialogService.open(DialogAlertComponent, {
          context: { title: 'Sucesso', message: 'Turma cadastrada com sucesso!' },
        });
      });
    }
  }

  fechar() {
    this.ref.close(false);
  }
}
